import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PaperPlaneTilt, Phone, Envelope, MapPin, Spinner, ChatCircleDots } from "@phosphor-icons/react";
import { fmtDate, portalErr } from "@/lib/portalApi";

export default function PortalKontakt({ api, me, onChanged }) {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const k = me?.kanzlei || {};

  const load = async () => {
    try { const r = await api.get("/portal/nachrichten"); setMsgs(r.data); onChanged?.(); }
    catch (e) { toast.error(portalErr(e, "Nachrichten konnten nicht geladen werden")); setMsgs([]); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/portal/nachrichten", { text: text.trim() });
      setMsgs((m) => [...(m || []), r.data]);
      setText("");
      toast.success("Nachricht an Ihre Kanzlei gesendet");
    } catch (e) { toast.error(portalErr(e, "Nachricht konnte nicht gesendet werden")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="portal-kontakt">
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Kontakt</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">Ihre Kanzlei</h1>
      </div>

      <section className="bg-[#1A2238] text-white rounded-2xl p-5 sm:p-7" data-testid="portal-kanzlei-card">
        <p className="font-serif text-2xl">{k.name || "Kanzlei"}</p>
        {k.anwalt && <p className="text-sm text-white/65 mt-0.5">{k.anwalt}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
          {k.telefon && <a href={`tel:${k.telefon}`} className="flex items-center gap-2.5 bg-white/10 hover:bg-white/15 rounded-xl px-4 py-3 text-sm transition-colors" data-testid="portal-kanzlei-telefon"><Phone size={18} weight="duotone" /> {k.telefon}</a>}
          {k.email && <a href={`mailto:${k.email}`} className="flex items-center gap-2.5 bg-white/10 hover:bg-white/15 rounded-xl px-4 py-3 text-sm transition-colors truncate" data-testid="portal-kanzlei-email"><Envelope size={18} weight="duotone" /> <span className="truncate">{k.email}</span></a>}
          {(k.adresse || k.ort) && <span className="flex items-center gap-2.5 bg-white/10 rounded-xl px-4 py-3 text-sm"><MapPin size={18} weight="duotone" /> {[k.adresse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</span>}
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid="portal-nachrichten">
        <div className="px-5 pt-5 pb-3 flex items-center gap-2">
          <ChatCircleDots size={18} weight="duotone" className="text-[#1A2238]" />
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9CA3AF]">Nachrichten</p>
        </div>
        <div className="px-5 max-h-[50vh] overflow-y-auto space-y-3 pb-3" data-testid="portal-nachrichten-liste">
          {msgs === null ? (
            <p className="text-sm text-[#6B7280] py-4 flex items-center gap-2"><Spinner size={16} className="animate-spin" /> Lädt …</p>
          ) : msgs.length === 0 ? (
            <p className="text-sm text-[#6B7280] py-4" data-testid="portal-nachrichten-leer">Noch keine Nachrichten. Schreiben Sie uns — wir antworten so schnell wie möglich.</p>
          ) : msgs.map((m) => {
            const mine = m.von === "mandant";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`portal-msg-${m.von}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${mine ? "bg-[#1A2238] text-white rounded-br-sm" : "bg-[#F4F3EF] text-[#0A192F] rounded-bl-sm"}`}>
                  {!mine && m.absender && <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">{m.absender}</p>}
                  <p className="whitespace-pre-line">{m.text}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-white/55" : "text-[#9CA3AF]"}`}>{fmtDate(m.created_at, true)}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="border-t border-[#F3F4F6] p-4 space-y-3 bg-[#FAFAF8]">
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ihre Nachricht an die Kanzlei …" className="bg-white" data-testid="portal-msg-input" />
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-[#9CA3AF]">Für dringende Anliegen rufen Sie bitte an.</p>
            <Button onClick={send} disabled={busy || !text.trim()} className="rounded-xl bg-[#1A2238] hover:bg-[#2C3859]" data-testid="portal-msg-send">
              {busy ? <Spinner size={16} className="animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" weight="duotone" />} Senden
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
